/**
 * Planta do Mapa da Produção: onde cada coisa fica DENTRO de cada sala.
 *
 * A sala é a FORMA que o gestor desenhou (`dados/forma.js`: retângulo, elipse
 * ou polígono a mão livre). O conteúdo continua sendo retângulos em grade, então
 * ele é montado dentro da MAIOR CAIXA INSCRITA na forma (`inscrita.js`) — é o
 * que faz uma sala em "L" ou redonda receber cabeçalho, bancadas e itens sem
 * nada furar a parede. Num setor retangular a caixa inscrita É a sala, e o
 * desenho sai igual ao da v2.
 *
 * Dentro dessa caixa: cabeçalho (nome, número, alertas) e um corpo dividido em
 * ÁREAS — um subsetor por área, "Postos" para posto sem subsetor e a "Fila" (o
 * que chegou pela planilha e o gestor ainda não distribuiu). Nas áreas ficam as
 * BANCADAS (postos), com as pessoas e os ITENS em cima.
 *
 * Dois tipos de item andam pelo mapa e dividem os mesmos espaços:
 *
 *   AMBIENTE — a linha da planilha (ficha do tamanho do espaço);
 *   PEÇA     — a peça desmembrada do ambiente (`dados/pecas.js`), uma pecinha
 *              MENOR, duas por espaço, porque é um derivado e não um ambiente.
 *
 * Tudo em coordenadas do mundo. As medidas-base são multiplicadas por uma
 * escala interna `k`: para cada sala testamos algumas escalas e orientações e
 * ficamos com a que mostra todas as bancadas e o máximo de itens sem encolher
 * demais (pontuarTentativa). O que não cabe num lugar vira um "+N" no último
 * espaço. Função pura: nada de DOM.
 */
import { caixaDeVarias, caixaEnvolvente, normalizar } from "./dados/forma";
import { STATUS_AMBIENTE } from "./dados/vocabulario";
import { caixaInscrita, maiorEspacoLivre } from "./inscrita";
import { SITUACAO } from "./setores";
import { cenaDoSetor } from "./setor/plantaDoSetor";

export const MEDIDA = Object.freeze({
  PAD: 16,
  CABECALHO: 96,
  GAP: 14,
  AREA_PAD: 12,
  AREA_ROTULO: 30,
  POSTO_L: 204,
  POSTO_A: 150,
  PECA_L: 118,
  PECA_A: 30,
  PECA_GAP: 8,
});

// Medidas dentro da bancada, antes de multiplicar por `k`.
export const BANCADA = Object.freeze({
  PAD: 10,
  PESSOA_Y: 30,
  PESSOA_A: 36,
  PESSOAS_MAX: 2,
  PESSOAS_MAX_SEM_PECAS: 3,
  PECAS_Y: 108,
  PECA_A: 30,
  PECA_GAP: 8,
  VAGAS_PECAS: 2,
  AVATAR_R: 13,
});

export const TIPO_AREA = Object.freeze({ SUBSETOR: "subsetor", POSTOS: "postos", FILA: "fila" });
export const CONTEINER_FILA = "fila";

/**
 * Quantas peças desmembradas dividem um espaço de ambiente. Duas: a pecinha
 * ainda cabe com um nome curto legível, e o espaço não vira mosaico.
 */
export const PECAS_POR_VAGA = 2;
// Altura da pecinha em frações do espaço: ela é menor de propósito — é o que
// diz "isto é um pedaço de um ambiente", antes de qualquer cor ou rótulo.
const FRACAO_ALTURA_PECA = 0.74;
const FRACAO_GAP_PECA = 0.21;

const ESCALAS = [1, 0.9, 0.8, 0.7, 0.6, 0.5];
// Peso da escala contra a fração de itens visíveis: sem ele a planta
// encolheria tudo só para mostrar mais um item. Tem de ficar ABAIXO de 1
// (a fração inteira): com 1,5 uma sala que cabia 1 vaga na escala cheia
// preferia não mostrar item nenhum — 18 ambientes viravam só "+18".
const PESO_ESCALA = 0.35;
const CABECALHO_REFERENCIA = { largura: 560, altura: 300, min: 0.45, max: 1.2 };
// A fila no modo "pilha" (bancadas em cima, fila embaixo) ocupa ao menos isso.
const FRACAO_FILA_PILHA = 0.3;
const LINHAS_FILA_PILHA = 2;
const DISTANCIA_ENTRADA = 160;
const FOLGA_CONECTOR = 10;
// Largura média de um caractere da Sora, em "em" — para truncar texto no SVG.
const CARACTERE_EM = 0.6;

const limitar = (v, min, max) => Math.min(max, Math.max(min, v));
const soma = (valores) => valores.reduce((total, v) => total + v, 0);
// Folga de ponto flutuante: 968.4 + 12.6 dividido por 196.2 tem de dar 5, não 4,999….
const EPSILON = 1e-6;
const caberem = (espaco, item, gap) => Math.max(0, Math.floor((espaco + gap + EPSILON) / (item + gap)));
const centro = (r) => ({ x: r.x + r.largura / 2, y: r.y + r.altura / 2 });

/** Chave da peça desmembrada no mapa de posições (o ambiente usa a `chaveLinha`). */
export const chaveDaPeca = (id) => `peca:${id}`;

/** Largura estimada de um texto no SVG (sem medir o DOM). */
export function larguraTexto(texto, fonte) {
  return String(texto ?? "").length * fonte * CARACTERE_EM;
}

/** Corta o texto para caber em `largura` com fonte `fonte` (com reticências). */
export function truncarTexto(texto, largura, fonte) {
  const limpo = String(texto ?? "").trim();
  const cabem = Math.floor(largura / (fonte * CARACTERE_EM));
  if (cabem <= 0) return "";
  if (limpo.length <= cabem) return limpo;
  return cabem <= 1 ? "…" : `${limpo.slice(0, cabem - 1).trimEnd()}…`;
}

// ------------------------------------------------------------- áreas

const porNome = (a, b) => String(a.nome).localeCompare(String(b.nome), "pt-BR", { numeric: true });
const porOrdemENome = (a, b) => (a.ordem ?? 0) - (b.ordem ?? 0) || porNome(a, b);

/**
 * Áreas da sala, na ordem de desenho: subsetores ativos (por ordem), depois
 * "Postos" (postos sem subsetor ativo + a equipe do setor que não tem posto,
 * numa bancada virtual "Sem posto") e por último a Fila.
 *
 * `setorId` é o id do setor cadastrado (na v2 era o código fixo).
 */
export function areasDaSala(setorId, estado) {
  const ativo = (item) => item.ativo !== false;
  const doSetor = (item) => item.setor_id === setorId;
  const subsetores = (estado?.subsetores || []).filter((s) => doSetor(s) && ativo(s)).sort(porOrdemENome);
  const idsSubsetores = new Set(subsetores.map((s) => s.id));
  const colaboradoresAtivos = (estado?.colaboradores || []).filter(ativo);
  const idsAtivos = new Set(colaboradoresAtivos.map((c) => c.id));
  const postos = (estado?.postos || []).filter((p) => doSetor(p) && ativo(p)).sort(porOrdemENome);

  const bancada = (posto) => ({
    id: posto.id,
    nome: posto.nome,
    virtual: false,
    colaboradorIds: (posto.colaborador_ids || []).filter((id) => idsAtivos.has(id)),
    // O desenho que o gestor fez na visão Mapa do setor (vazio = nunca desenhou).
    forma: posto.forma || null,
  });
  const areas = subsetores.map((s) => ({
    id: `subsetor:${s.id}`,
    tipo: TIPO_AREA.SUBSETOR,
    subsetorId: s.id,
    rotulo: s.nome,
    forma: s.forma || null,
    bancadas: postos.filter((p) => p.subsetor_id === s.id).map(bancada),
  }));

  const soltos = postos.filter((p) => !p.subsetor_id || !idsSubsetores.has(p.subsetor_id)).map(bancada);
  const comPosto = new Set(postos.flatMap((p) => p.colaborador_ids || []));
  const semPosto = colaboradoresAtivos
    .filter((c) => (c.setor_ids || []).includes(setorId) && !comPosto.has(c.id))
    .sort(porNome)
    .map((c) => c.id);
  if (semPosto.length) {
    soltos.push({ id: `sem-posto:${setorId}`, nome: "Sem posto", virtual: true, colaboradorIds: semPosto });
  }
  if (soltos.length) {
    areas.push({ id: "postos", tipo: TIPO_AREA.POSTOS, subsetorId: null, rotulo: "Postos", bancadas: soltos });
  }
  areas.push({ id: CONTEINER_FILA, tipo: TIPO_AREA.FILA, subsetorId: null, rotulo: "Fila", bancadas: [] });
  return areas;
}

// ------------------------------------------------ ordem dos itens

const PRIORIDADE_STATUS = {
  [STATUS_AMBIENTE.PAUSADO]: 0,
  [STATUS_AMBIENTE.EM_PRODUCAO]: 1,
  [STATUS_AMBIENTE.AGUARDANDO]: 2,
};

function compararPrazo(a, b) {
  const ta = a?.getTime?.() ?? Number.POSITIVE_INFINITY;
  const tb = b?.getTime?.() ?? Number.POSITIVE_INFINITY;
  if (ta === tb) return 0;
  return ta < tb ? -1 : 1;
}

/**
 * Ordem em que os ambientes ocupam os espaços (o resto vai para o "+N"): o que
 * passa no filtro primeiro, depois atrasadas, pausadas, em produção,
 * aguardando; empate pelo prazo efetivo e pelo rótulo.
 */
export function ordenarParaExibir(demandas, passa = null) {
  return [...demandas].sort((a, b) => {
    if (passa) {
      const pa = passa.has(a.chaveLinha) ? 0 : 1;
      const pb = passa.has(b.chaveLinha) ? 0 : 1;
      if (pa !== pb) return pa - pb;
    }
    if (a.atrasada !== b.atrasada) return a.atrasada ? -1 : 1;
    const sa = PRIORIDADE_STATUS[a.statusApp] ?? 3;
    const sb = PRIORIDADE_STATUS[b.statusApp] ?? 3;
    return (
      sa - sb ||
      compararPrazo(a.prazoEfetivo, b.prazoEfetivo) ||
      String(a.rotulo || "").localeCompare(String(b.rotulo || ""), "pt-BR", { numeric: true }) ||
      String(a.chaveLinha).localeCompare(String(b.chaveLinha))
    );
  });
}

/**
 * Ordem das peças desmembradas: o que passa no filtro primeiro; o resto mantém
 * a ordem que `dados/pecas.js` já entregou (fora, atrasada, prioridade, prazo).
 */
export function ordenarPecasParaExibir(pecas, passa = null) {
  if (!passa) return [...pecas];
  return [...pecas].sort((a, b) => (passa.has(a.id) ? 0 : 1) - (passa.has(b.id) ? 0 : 1));
}

/** Em que lugar da sala o item fica: bancada, chão do subsetor ou fila. */
export function conteinerDoItem(item, areas) {
  const bancadaValida = areas.some((a) => a.bancadas.some((b) => !b.virtual && b.id === item.postoId));
  if (item.postoId && bancadaValida) return `bancada:${item.postoId}`;
  const subsetorValido = areas.some((a) => a.tipo === TIPO_AREA.SUBSETOR && a.subsetorId === item.subsetorId);
  if (item.subsetorId && subsetorValido) return `chao:subsetor:${item.subsetorId}`;
  return CONTEINER_FILA;
}

function agruparPorConteiner(itens, areas) {
  const mapa = new Map();
  itens.forEach((item) => {
    const id = conteinerDoItem(item, areas);
    if (!mapa.has(id)) mapa.set(id, []);
    mapa.get(id).push(item);
  });
  return mapa;
}

/** Quantos espaços um lugar precisa: um por ambiente, meio por peça. */
export function vagasNecessarias(ambientes, pecas) {
  return ambientes + Math.ceil(pecas / PECAS_POR_VAGA);
}

function contarVagas(gruposAmbientes, gruposPecas) {
  const contagens = new Map();
  const chaves = new Set([...gruposAmbientes.keys(), ...gruposPecas.keys()]);
  chaves.forEach((conteiner) => {
    const ambientes = (gruposAmbientes.get(conteiner) || []).length;
    const pecas = (gruposPecas.get(conteiner) || []).length;
    contagens.set(conteiner, vagasNecessarias(ambientes, pecas));
  });
  return contagens;
}

// Quantos ITENS estão em cada lugar. Diferente de `contarVagas`, que conta
// ESPAÇOS (duas peças cabem num só): o rótulo da área mostra item, não espaço —
// "Fila · 3" com dois ambientes e duas peças seria mentira.
function contarItens(gruposAmbientes, gruposPecas) {
  const contagens = new Map();
  const chaves = new Set([...gruposAmbientes.keys(), ...gruposPecas.keys()]);
  chaves.forEach((conteiner) => {
    const ambientes = (gruposAmbientes.get(conteiner) || []).length;
    const pecas = (gruposPecas.get(conteiner) || []).length;
    contagens.set(conteiner, ambientes + pecas);
  });
  return contagens;
}

// ------------------------------------------------------- geometria

function medidasEm(k) {
  return {
    k,
    areaPad: MEDIDA.AREA_PAD * k,
    rotulo: MEDIDA.AREA_ROTULO * k,
    gap: MEDIDA.GAP * k,
    postoL: MEDIDA.POSTO_L * k,
    postoA: MEDIDA.POSTO_A * k,
    pecaL: MEDIDA.PECA_L * k,
    pecaA: MEDIDA.PECA_A * k,
    pecaGap: MEDIDA.PECA_GAP * k,
  };
}

const idChao = (area) => `chao:${area.id}`;
const soPecas = (area) => area.tipo === TIPO_AREA.FILA || area.bancadas.length === 0;

// Horizontal: dada a altura da área, a largura mínima para caber tudo.
function larguraNecessaria(area, altura, m, contagens) {
  const util = altura - m.rotulo - m.areaPad;
  const minima = 2 * m.areaPad + m.pecaL;
  if (soPecas(area)) return util >= m.pecaA ? minima : null;
  const temChao = (contagens.get(idChao(area)) || 0) > 0;
  const linhas = caberem(util - (temChao ? m.pecaA + m.gap : 0), m.postoA, m.gap);
  if (linhas < 1) return null;
  const colunas = Math.ceil(area.bancadas.length / linhas);
  return Math.max(minima, 2 * m.areaPad + colunas * m.postoL + (colunas - 1) * m.gap);
}

// Vertical: dada a largura da área, a altura mínima para caber tudo.
function alturaNecessaria(area, largura, m, contagens) {
  const util = largura - 2 * m.areaPad;
  if (util < m.pecaL) return null;
  const minima = m.rotulo + m.areaPad + m.pecaA;
  if (soPecas(area)) return minima;
  const colunas = caberem(util, m.postoL, m.gap);
  if (colunas < 1) return null;
  const linhas = Math.ceil(area.bancadas.length / colunas);
  const temChao = (contagens.get(idChao(area)) || 0) > 0;
  return m.rotulo + m.areaPad + linhas * m.postoA + (linhas - 1) * m.gap + (temChao ? m.gap + m.pecaA : 0);
}

// O que sobra vai para quem tem item para mostrar (fila e chão).
function pesoDaSobra(area, contagens) {
  if (area.tipo === TIPO_AREA.FILA) return Math.max(1, contagens.get(CONTEINER_FILA) || 0);
  const noChao = contagens.get(idChao(area)) || 0;
  return area.bancadas.length === 0 ? noChao : noChao * 0.5;
}

function distribuir(areas, necessarios, disponivel, gap, contagens) {
  const ocupado = soma(necessarios) + gap * (areas.length - 1);
  if (ocupado > disponivel + 0.001) return null;
  const sobra = disponivel - ocupado;
  const pesos = areas.map((a) => pesoDaSobra(a, contagens));
  const totalPesos = soma(pesos);
  return necessarios.map((n, i) => n + (totalPesos > 0 ? (sobra * pesos[i]) / totalPesos : sobra / areas.length));
}

/** Espaços de item numa grade, esticando a largura para ocupar a linha. */
export function gradeDeVagas(rect, largura, altura, gap) {
  const colunas = caberem(rect.largura, largura, gap);
  const linhas = caberem(rect.altura, altura, gap);
  if (colunas < 1 || linhas < 1) return [];
  const larguraReal = Math.min(largura * 1.35, (rect.largura - (colunas - 1) * gap) / colunas);
  const vagas = [];
  for (let l = 0; l < linhas; l += 1) {
    for (let c = 0; c < colunas; c += 1) {
      vagas.push({ x: rect.x + c * (larguraReal + gap), y: rect.y + l * (altura + gap), largura: larguraReal, altura });
    }
  }
  return vagas;
}

/**
 * Um espaço de ambiente dividido em espaços de PEÇA: até duas, lado a lado,
 * mais baixas e centradas na vertical.
 */
export function vagasDePeca(vaga, quantas) {
  const total = Math.min(PECAS_POR_VAGA, Math.max(1, quantas));
  const gap = total > 1 ? vaga.altura * FRACAO_GAP_PECA : 0;
  const largura = (vaga.largura - (total - 1) * gap) / total;
  const altura = vaga.altura * FRACAO_ALTURA_PECA;
  const y = vaga.y + (vaga.altura - altura) / 2;
  return Array.from({ length: total }, (_, i) => ({ x: vaga.x + i * (largura + gap), y, largura, altura }));
}

function pessoasDaBancada(bancada, rect, k) {
  const maximo = bancada.virtual ? BANCADA.PESSOAS_MAX_SEM_PECAS : BANCADA.PESSOAS_MAX;
  const visiveis = bancada.colaboradorIds.slice(0, maximo);
  const pad = BANCADA.PAD * k;
  const pessoas = visiveis.map((colaboradorId, i) => {
    const y = rect.y + (BANCADA.PESSOA_Y + i * BANCADA.PESSOA_A) * k;
    const r = BANCADA.AVATAR_R * k;
    return {
      colaboradorId,
      x: rect.x + pad,
      y,
      largura: rect.largura - 2 * pad,
      altura: (BANCADA.PESSOA_A - 2) * k,
      cx: rect.x + pad + r + k,
      cy: y + (BANCADA.PESSOA_A / 2 - 1) * k,
      r,
    };
  });
  return { pessoas, pessoasOcultas: bancada.colaboradorIds.length - visiveis.length };
}

// Um item só ocupa a bancada inteira (o rótulo cabe); dois ou mais dividem.
function vagasDaBancada(rect, k, itensNaBancada) {
  const pad = BANCADA.PAD * k;
  const gap = BANCADA.PECA_GAP * k;
  const n = itensNaBancada <= 1 ? 1 : BANCADA.VAGAS_PECAS;
  const largura = (rect.largura - 2 * pad - (n - 1) * gap) / n;
  return Array.from({ length: n }, (_, i) => ({
    x: rect.x + pad + i * (largura + gap),
    y: rect.y + BANCADA.PECAS_Y * k,
    largura,
    altura: BANCADA.PECA_A * k,
  }));
}

function montarBancadas(area, conteudo, m, contagens) {
  const colunas = Math.max(1, caberem(conteudo.largura, m.postoL, m.gap));
  const linhas = caberem(conteudo.altura, m.postoA, m.gap);
  const cabem = Math.min(area.bancadas.length, colunas * linhas);
  const bancadas = area.bancadas.slice(0, cabem).map((b, i) => {
    const rect = {
      x: conteudo.x + (i % colunas) * (m.postoL + m.gap),
      y: conteudo.y + Math.floor(i / colunas) * (m.postoA + m.gap),
      largura: m.postoL,
      altura: m.postoA,
    };
    return {
      ...b,
      ...rect,
      areaId: area.id,
      conteiner: b.virtual ? null : `bancada:${b.id}`,
      ...pessoasDaBancada(b, rect, m.k),
      vagas: b.virtual ? [] : vagasDaBancada(rect, m.k, contagens.get(`bancada:${b.id}`) || 0),
    };
  });
  const linhasUsadas = Math.ceil(cabem / colunas);
  const alturaUsada = linhasUsadas * m.postoA + Math.max(0, linhasUsadas - 1) * m.gap;
  const ocultasIds = area.bancadas.slice(cabem).filter((b) => !b.virtual).map((b) => `bancada:${b.id}`);
  return { bancadas, ocultas: area.bancadas.length - cabem, ocultasIds, alturaUsada };
}

function montarArea(area, rect, m, contagens) {
  const conteudo = {
    x: rect.x + m.areaPad,
    y: rect.y + m.rotulo,
    largura: rect.largura - 2 * m.areaPad,
    altura: rect.altura - m.rotulo - m.areaPad,
  };
  const base = { ...area, ...rect, bancadasOcultas: 0, bancadas: [] };
  if (soPecas(area)) {
    const conteiner = area.tipo === TIPO_AREA.FILA ? CONTEINER_FILA : idChao(area);
    return { ...base, vagasChao: { conteiner, vagas: gradeDeVagas(conteudo, m.pecaL, m.pecaA, m.pecaGap) } };
  }
  const temChao = (contagens.get(idChao(area)) || 0) > 0;
  const regiao = { ...conteudo, altura: conteudo.altura - (temChao ? m.pecaA + m.gap : 0) };
  const { bancadas, ocultas, ocultasIds, alturaUsada } = montarBancadas(area, regiao, m, contagens);
  const chao = {
    x: conteudo.x,
    y: conteudo.y + alturaUsada + m.gap,
    largura: conteudo.largura,
    altura: conteudo.altura - alturaUsada - m.gap,
  };
  return {
    ...base,
    bancadas,
    bancadasOcultas: ocultas,
    bancadasOcultasIds: ocultasIds,
    vagasChao: { conteiner: idChao(area), vagas: temChao ? gradeDeVagas(chao, m.pecaL, m.pecaA, m.pecaGap) : [] },
  };
}

function emLinha(corpo, areas, m, horizontal, contagens) {
  const principal = horizontal ? corpo.largura : corpo.altura;
  const cruzado = horizontal ? corpo.altura : corpo.largura;
  const necessarios = areas.map((a) =>
    horizontal ? larguraNecessaria(a, cruzado, m, contagens) : alturaNecessaria(a, cruzado, m, contagens)
  );
  if (necessarios.some((n) => n == null)) return null;
  const tamanhos = distribuir(areas, necessarios, principal, m.gap, contagens);
  if (!tamanhos) return null;
  let cursor = horizontal ? corpo.x : corpo.y;
  return areas.map((area, i) => {
    const rect = horizontal
      ? { x: cursor, y: corpo.y, largura: tamanhos[i], altura: corpo.altura }
      : { x: corpo.x, y: cursor, largura: corpo.largura, altura: tamanhos[i] };
    cursor += tamanhos[i] + m.gap;
    return montarArea(area, rect, m, contagens);
  });
}

// Bancadas lado a lado em cima e a fila como uma faixa larga embaixo.
function emPilha(corpo, areas, m, contagens) {
  const fila = areas[areas.length - 1];
  const outras = areas.slice(0, -1);
  if (outras.length === 0) return null;
  const minimaFila = m.rotulo + m.areaPad + LINHAS_FILA_PILHA * m.pecaA + (LINHAS_FILA_PILHA - 1) * m.pecaGap;
  const alturaFila = Math.max(minimaFila, corpo.altura * FRACAO_FILA_PILHA);
  const alturaTopo = corpo.altura - alturaFila - m.gap;
  if (alturaTopo <= 0) return null;
  const topo = emLinha({ ...corpo, altura: alturaTopo }, outras, m, true, contagens);
  if (!topo) return null;
  const rectFila = { x: corpo.x, y: corpo.y + alturaTopo + m.gap, largura: corpo.largura, altura: alturaFila };
  return [...topo, montarArea(fila, rectFila, m, contagens)];
}

function vagasPorConteiner(areas) {
  const mapa = new Map();
  areas.forEach((area) => {
    if (area.vagasChao) mapa.set(area.vagasChao.conteiner, area.vagasChao.vagas);
    area.bancadas.forEach((b) => {
      if (b.conteiner) mapa.set(b.conteiner, b.vagas);
    });
  });
  return mapa;
}

function visiveisNo(n, vagas) {
  if (n <= vagas) return n;
  return Math.max(0, vagas - 1);
}

/** Nota da tentativa: fração de espaços necessários atendidos + peso da escala. */
export function pontuarTentativa(areas, contagens, k) {
  const vagas = vagasPorConteiner(areas);
  const total = soma([...contagens.values()]);
  const visiveis = soma([...contagens.entries()].map(([id, n]) => visiveisNo(n, (vagas.get(id) || []).length)));
  const fracao = total > 0 ? visiveis / total : 1;
  return fracao + PESO_ESCALA * k;
}

function melhorTentativa(corpo, areas, contagens) {
  const horizontalPrimeiro = corpo.largura >= corpo.altura;
  let melhor = null;
  ESCALAS.forEach((k) => {
    const m = medidasEm(k);
    const tentativas = [
      emLinha(corpo, areas, m, horizontalPrimeiro, contagens),
      emPilha(corpo, areas, m, contagens),
      emLinha(corpo, areas, m, !horizontalPrimeiro, contagens),
    ];
    tentativas.forEach((resultado) => {
      if (!resultado) return;
      const nota = pontuarTentativa(resultado, contagens, k);
      if (!melhor || nota > melhor.nota + 1e-9) melhor = { nota, k, areas: resultado };
    });
  });
  return melhor;
}

// ------------------------------------------------------------- itens

function fichaDoAmbiente(demanda, setorId, vaga, k) {
  return { chaveLinha: demanda.chaveLinha, chave: demanda.chave, demanda, setorId, k, ...vaga };
}

function fichaDaPeca(peca, setorId, vaga, k) {
  return { id: peca.id, peca, setorId, k, ...vaga };
}

// Item de um lugar que ficou sem espaço desenhado (bancada que não coube)
// vai para a fila, para nunca sumir sem um "+N" que o conte.
function semLugarParaFila(grupos, vagas) {
  const resultado = new Map();
  const sobras = [];
  grupos.forEach((lista, conteiner) => {
    if (conteiner !== CONTEINER_FILA && !(vagas.get(conteiner) || []).length) sobras.push(...lista);
    else resultado.set(conteiner, lista);
  });
  if (sobras.length) resultado.set(CONTEINER_FILA, [...(resultado.get(CONTEINER_FILA) || []), ...sobras]);
  return resultado;
}

function posicionarItens(setorId, gruposAmbientesBrutos, gruposPecasBrutos, vagas, k, reserva) {
  const ambientes = [];
  const pecas = [];
  const mais = [];
  const posicoes = new Map();
  const gruposAmbientes = semLugarParaFila(gruposAmbientesBrutos, vagas);
  const gruposPecas = semLugarParaFila(gruposPecasBrutos, vagas);
  const conteineres = new Set([...gruposAmbientes.keys(), ...gruposPecas.keys()]);

  conteineres.forEach((conteiner) => {
    const daVez = gruposAmbientes.get(conteiner) || [];
    const pecasDaVez = gruposPecas.get(conteiner) || [];
    const espacos = vagas.get(conteiner) || [];
    if (espacos.length === 0) {
      daVez.forEach((d) => posicoes.set(d.chaveLinha, reserva));
      pecasDaVez.forEach((p) => posicoes.set(chaveDaPeca(p.id), reserva));
      return;
    }
    const cabeTudo = vagasNecessarias(daVez.length, pecasDaVez.length) <= espacos.length;
    // Sem caber tudo, o último espaço é do "+N": some-lo esconderia trabalho.
    const disponiveis = cabeTudo ? espacos.length : espacos.length - 1;
    const quantosAmbientes = Math.min(daVez.length, disponiveis);
    const espacosDePecas = espacos.slice(quantosAmbientes, disponiveis);
    const quantasPecas = Math.min(pecasDaVez.length, espacosDePecas.length * PECAS_POR_VAGA);

    daVez.slice(0, quantosAmbientes).forEach((d, i) => {
      ambientes.push(fichaDoAmbiente(d, setorId, espacos[i], k));
      posicoes.set(d.chaveLinha, { x: espacos[i].x, y: espacos[i].y });
    });
    pecasDaVez.slice(0, quantasPecas).forEach((p, i) => {
      const indiceDoEspaco = Math.floor(i / PECAS_POR_VAGA);
      const noEspaco = quantasPecas - indiceDoEspaco * PECAS_POR_VAGA;
      const vaga = vagasDePeca(espacosDePecas[indiceDoEspaco], noEspaco)[i % PECAS_POR_VAGA];
      pecas.push(fichaDaPeca(p, setorId, vaga, k));
      posicoes.set(chaveDaPeca(p.id), { x: vaga.x, y: vaga.y });
    });

    const ocultas = daVez.slice(quantosAmbientes);
    const ocultasPecas = pecasDaVez.slice(quantasPecas);
    if (ocultas.length === 0 && ocultasPecas.length === 0) return;
    const vaga = espacos[espacos.length - 1];
    mais.push({ id: `${setorId}:${conteiner}`, setorId, conteiner, k, ...vaga, ocultas, ocultasPecas });
    ocultas.forEach((d) => posicoes.set(d.chaveLinha, { x: vaga.x, y: vaga.y }));
    ocultasPecas.forEach((p) => posicoes.set(chaveDaPeca(p.id), { x: vaga.x, y: vaga.y }));
  });
  return { ambientes, pecas, mais, posicoes };
}

// Itens da área: os das bancadas (inclusive as que não couberam) + o chão.
function totalDaArea(area, itens) {
  const conteineres = [area.vagasChao?.conteiner, ...area.bancadas.map((b) => b.conteiner)].filter(Boolean);
  const ocultas = area.bancadasOcultasIds || [];
  return soma([...conteineres, ...ocultas].map((id) => itens.get(id) || 0));
}

function escalaDoCabecalho(interior) {
  const { largura, altura, min, max } = CABECALHO_REFERENCIA;
  return limitar(Math.min(interior.largura / largura, interior.altura / altura), min, max);
}

/**
 * Geometria fixa da sala: a caixa ENVOLVENTE (onde ficam a parede desenhada e
 * a alça de redimensionar) e o INTERIOR (a maior caixa inscrita na forma, onde
 * cabeçalho e corpo são montados). Nas salas retangulares as duas coincidem.
 */
function geometriaBase(elemento, areas) {
  const caixa = caixaEnvolvente(elemento.forma) || { x: 0, y: 0, largura: 0, altura: 0 };
  const interior = caixaInscrita(elemento.forma) || caixa;
  const hk = escalaDoCabecalho(interior);
  const cabecalho = { x: interior.x, y: interior.y, largura: interior.largura, altura: MEDIDA.CABECALHO * hk };
  return {
    setorId: elemento.setorId,
    nome: elemento.nome,
    etapa: elemento.etapa,
    etapaRotulo: elemento.etapaRotulo,
    recebeDaPlanilha: Boolean(elemento.recebeDaPlanilha),
    forma: elemento.forma,
    x: caixa.x,
    y: caixa.y,
    largura: caixa.largura,
    altura: caixa.altura,
    interior,
    hk,
    cabecalho,
    corpo: {
      x: interior.x + MEDIDA.PAD,
      y: interior.y + cabecalho.altura,
      largura: interior.largura - 2 * MEDIDA.PAD,
      altura: interior.altura - cabecalho.altura - MEDIDA.PAD,
    },
    // Sem subsetor nem posto cadastrado: a sala convida a configurar.
    semEstrutura: areas.every((a) => a.tipo !== TIPO_AREA.SUBSETOR && a.bancadas.every((b) => b.virtual)),
  };
}

// Menor "+N" que ainda se lê e se toca, em unidades do mundo.
const MAIS_MINIMO = Object.freeze({ largura: 26, altura: 12 });

/**
 * Canto para o "+N" quando nem um espaço de item cabe no corpo: encostado na
 * quina de baixo da CAIXA INSCRITA (logo, dentro da forma, seja ela qual for),
 * com o tamanho que sobrar. Sem isto a sala pequena engolia os ambientes em
 * silêncio — o cabeçalho dizia "4 ambientes, 4 atrasados" e não havia nada no
 * mapa para tocar e chegar até eles.
 */
function vagaDeEmergencia(base) {
  const { interior, cabecalho } = base;
  if (!interior || interior.largura <= 0 || interior.altura <= 0) return null;
  const folga = Math.min(MEDIDA.PAD / 2, interior.largura / 8, interior.altura / 8);
  const largura = Math.min(MEDIDA.PECA_L / 2, interior.largura - 2 * folga);
  const sobra = interior.altura - (cabecalho?.altura || 0) - 2 * folga;
  const altura = Math.min(MEDIDA.PECA_A, Math.max(MAIS_MINIMO.altura, sobra));
  if (largura < MAIS_MINIMO.largura || altura > interior.altura - folga) return null;
  return {
    x: interior.x + interior.largura - folga - largura,
    y: interior.y + interior.altura - folga - altura,
    largura,
    altura,
  };
}

// Sala pequena demais para a menor escala: só o cabeçalho e um "+N" com tudo.
function salaCompacta(base, demandas, pecas) {
  const m = medidasEm(ESCALAS[ESCALAS.length - 1]);
  const vaga = gradeDeVagas(base.corpo, m.pecaL, m.pecaA, m.pecaGap)[0] || vagaDeEmergencia(base);
  const reserva = vaga ? { x: vaga.x, y: vaga.y } : centro(base);
  const temItem = demandas.length > 0 || pecas.length > 0;
  const mais = vaga && temItem
    ? [{
      id: `${base.setorId}:${CONTEINER_FILA}`,
      setorId: base.setorId,
      conteiner: CONTEINER_FILA,
      // O "+N" de emergência é mais baixo que um espaço normal: o k acompanha
      // a altura dele, senão o número sairia por cima da borda da ficha.
      k: Math.min(m.k, vaga.altura / MEDIDA.PECA_A),
      ...vaga,
      ocultas: demandas,
      ocultasPecas: pecas,
    }]
    : [];
  const posicoes = new Map([
    ...demandas.map((d) => [d.chaveLinha, reserva]),
    ...pecas.map((p) => [chaveDaPeca(p.id), reserva]),
  ]);
  return { sala: { ...base, k: m.k, compacto: true, areas: [] }, ambientes: [], pecas: [], mais, posicoes };
}

/**
 * Uma sala montada. `demandas` = ambientes combinados que estão NESTE setor,
 * `pecas` = peças desmembradas que estão neste setor, os dois já na ordem de
 * exibição. Devolve a geometria da sala e os itens posicionados:
 * {sala, ambientes, pecas, mais, posicoes: Map<chave, {x,y}>}. `compacto: true`
 * quando nem a menor escala cabe (sala pequena): só o cabeçalho e um "+N".
 */
export function montarSala(elemento, { demandas = [], pecas = [], estado = null } = {}) {
  const areas = areasDaSala(elemento.setorId, estado);
  const base = geometriaBase(elemento, areas);
  const gruposAmbientes = agruparPorConteiner(demandas, areas);
  const gruposPecas = agruparPorConteiner(pecas, areas);
  const contagens = contarVagas(gruposAmbientes, gruposPecas);
  const itens = contarItens(gruposAmbientes, gruposPecas);
  // O gestor arrumou a sala na visão Mapa do setor: o mapa geral mostra O
  // DESENHO DELE, e não a montagem automática em colunas.
  const desenhada = layoutFoiDesenhado(areas)
    ? montarSalaDesenhada(base, completarComOSetor(areas, elemento, estado), contagens)
    : null;
  if (desenhada) {
    const vagas = vagasPorConteiner(desenhada.areas);
    const posicionados = posicionarItens(elemento.setorId, gruposAmbientes, gruposPecas, vagas, desenhada.k, centro(base));
    const areasComTotal = desenhada.areas.map((area) => ({ ...area, totalItens: totalDaArea(area, itens) }));
    return {
      sala: {
        ...base,
        cabecalho: desenhada.cabecalho,
        cabecalhoFora: desenhada.cabecalhoFora,
        hk: desenhada.hk,
        k: desenhada.k,
        compacto: false,
        desenhada: true,
        postosOcultos: false,
        areas: areasComTotal,
      },
      ...posicionados,
    };
  }
  const { corpo } = base;
  const cabe = corpo.largura > 0 && corpo.altura > 0;
  let melhor = cabe ? melhorTentativa(corpo, areas, contagens) : null;
  // Sala baixa demais para desenhar as bancadas: em vez de esconder TUDO
  // num "+N", desiste dos postos e mostra os itens. Quem quiser ver as
  // bancadas aumenta a sala no Organizar.
  let postosOcultos = false;
  if (!melhor && cabe) {
    const semBancadas = areas.map((area) => ({ ...area, bancadas: [] }));
    melhor = melhorTentativa(corpo, semBancadas, contagens);
    postosOcultos = Boolean(melhor);
  }
  if (!melhor) return salaCompacta(base, demandas, pecas);

  const vagas = vagasPorConteiner(melhor.areas);
  const posicionados = posicionarItens(elemento.setorId, gruposAmbientes, gruposPecas, vagas, melhor.k, centro(base));
  const areasComTotal = melhor.areas.map((area) => ({ ...area, totalItens: totalDaArea(area, itens) }));
  return {
    sala: { ...base, k: melhor.k, compacto: false, postosOcultos, areas: areasComTotal },
    ...posicionados,
  };
}

// ------------------------------------------------- sala desenhada

// Bancada desenhada pequena ainda mostra pessoa e item — só menores. Abaixo
// disto o avatar vira um ponto e ninguém lê nada.
const K_BANCADA_MINIMO = 0.2;
// A fila precisa de ao menos uma linha de itens para existir.
const LINHAS_FILA_DESENHADA = 1;

const temDesenho = (item) => Boolean(normalizar(item?.forma));

/**
 * O interior da sala foi DESENHADO quando o gestor pôs à mão pelo menos um
 * subsetor ou posto. Os que ele ainda não desenhou recebem a MESMA posição
 * automática da visão Mapa do setor (`completarComOSetor`) — assim as duas
 * telas mostram a mesma sala. Antes, um único posto sem desenho fazia o mapa
 * geral jogar fora o arranjo inteiro e voltar às colunas.
 */
export function layoutFoiDesenhado(areas) {
  const subsetores = (areas || []).filter((a) => a.tipo === TIPO_AREA.SUBSETOR);
  const postos = (areas || []).flatMap((a) => a.bancadas).filter((b) => !b.virtual);
  return subsetores.some(temDesenho) || postos.some(temDesenho);
}

/**
 * Completa a forma do que ainda não foi desenhado com a posição que a visão
 * Mapa do setor dá a ele (`cenaDoSetor`): uma regra só para as duas telas.
 */
function completarComOSetor(areas, elemento, estado) {
  const cena = cenaDoSetor({
    setor: { id: elemento.setorId, forma: elemento.forma },
    subsetores: estado?.subsetores || [],
    postos: estado?.postos || [],
  });
  const formas = new Map([...cena.subsetores, ...cena.postos].map((e) => [e.id, e.forma]));
  const completar = (item, id) => (temDesenho(item) ? item : { ...item, forma: formas.get(id) || item.forma });
  return areas.map((area) => ({
    ...(area.tipo === TIPO_AREA.SUBSETOR ? completar(area, area.subsetorId) : area),
    bancadas: area.bancadas.map((b) => (b.virtual ? b : completar(b, b.id))),
  }));
}

// A forma pode ser um "L" ou uma elipse: o conteúdo vai na caixa inscrita.
function caixaDoDesenho(forma) {
  const limpa = normalizar(forma);
  return caixaInscrita(limpa) || caixaEnvolvente(limpa);
}

function montarBancadaDesenhada(bancada, areaId, contagens) {
  const rect = caixaDoDesenho(bancada.forma);
  // Cada bancada tem a SUA escala: é do tamanho que o gestor desenhou, e o
  // que vai em cima dela (pessoas, item) acompanha.
  const k = Math.max(K_BANCADA_MINIMO, Math.min(1, rect.largura / MEDIDA.POSTO_L, rect.altura / MEDIDA.POSTO_A));
  const conteiner = `bancada:${bancada.id}`;
  return {
    ...bancada,
    ...rect,
    k,
    areaId,
    conteiner,
    ...pessoasDaBancada(bancada, rect, k),
    vagas: vagasDaBancada(rect, k, contagens.get(conteiner) || 0).map((vaga) => ({ ...vaga, k })),
  };
}

function montarAreaDesenhada(area, m, contagens) {
  const rect = caixaDoDesenho(area.forma);
  const conteudo = {
    x: rect.x + m.areaPad,
    y: rect.y + m.rotulo,
    largura: Math.max(0, rect.largura - 2 * m.areaPad),
    altura: Math.max(0, rect.altura - m.rotulo - m.areaPad),
  };
  const bancadas = area.bancadas.filter((b) => !b.virtual).map((b) => montarBancadaDesenhada(b, area.id, contagens));
  // O "chão" do subsetor (ambiente alocado nele, sem posto) fica no maior
  // pedaço que as bancadas desenhadas deixaram livre.
  const temChao = (contagens.get(idChao(area)) || 0) > 0;
  const livre = temChao ? maiorEspacoLivre(conteudo, bancadas, { folga: m.gap / 2 }) : null;
  return {
    ...area,
    ...rect,
    bancadas,
    bancadasOcultas: 0,
    bancadasOcultasIds: [],
    vagasChao: { conteiner: idChao(area), vagas: livre ? gradeDeVagas(livre, m.pecaL, m.pecaA, m.pecaGap) : [] },
  };
}

// Postos soltos (sem subsetor) desenhados: ficam onde foram desenhados, sem
// moldura de área em volta — uma moldura "Postos" mentiria sobre o desenho.
function postosSoltosDesenhados(areas, contagens) {
  const soltos = areas.find((a) => a.tipo === TIPO_AREA.POSTOS);
  const reais = soltos ? soltos.bancadas.filter((b) => !b.virtual) : [];
  if (!reais.length) return null;
  const bancadas = reais.map((b) => montarBancadaDesenhada(b, soltos.id, contagens));
  return {
    ...soltos,
    ...caixaDeVarias(bancadas),
    semMoldura: true,
    bancadas,
    bancadasOcultas: 0,
    bancadasOcultasIds: [],
    vagasChao: { conteiner: idChao(soltos), vagas: [] },
  };
}

// Menor espaço livre que ainda recebe o cabeçalho DENTRO da sala sem cortar o
// nome em duas letras. Abaixo disto a placa vai para fora (acima da parede).
const CABECALHO_DENTRO_MINIMO = Object.freeze({ largura: 220, altura: 70 });
// Placa do lado de fora: escala do texto e a largura que ela pode ocupar.
const PLACA = Object.freeze({ hk: 0.55, larguraMinima: 260, larguraMaxima: 420 });

/**
 * Sala que o gestor encheu de subsetores não tem onde pôr o cabeçalho: ele
 * vira uma PLACA logo acima da parede de cima, como a placa na porta da sala,
 * e a Fila vira uma caixinha ao lado dela. Assim o desenho de dentro fica
 * exatamente como o gestor fez — nada é desenhado por cima de um subsetor.
 */
function placaAcimaDaSala(base) {
  const altura = MEDIDA.CABECALHO * PLACA.hk;
  const largura = limitar(base.largura * 0.6, PLACA.larguraMinima, PLACA.larguraMaxima);
  const y = base.y - altura - MEDIDA.GAP;
  const alturaFila = MEDIDA.AREA_ROTULO + MEDIDA.PECA_A + MEDIDA.AREA_PAD;
  return {
    hk: PLACA.hk,
    fora: true,
    cabecalho: { x: base.x, y, largura, altura },
    // Alinhada pela base da placa: se crescesse para baixo, invadiria a sala.
    fila: {
      x: base.x + largura + MEDIDA.GAP,
      y: y + altura - alturaFila,
      largura: MEDIDA.PECA_L * 1.35 + 2 * MEDIDA.AREA_PAD,
      altura: alturaFila,
    },
  };
}

/**
 * Onde vão o cabeçalho e a Fila numa sala desenhada: no maior pedaço da sala
 * que os subsetores e postos do gestor não ocupam — ou, se não sobrou pedaço
 * que preste, numa placa acima dela. Não depende da escala dos itens, então é
 * calculado uma vez só por sala.
 */
function espacoDoCabecalhoEFila(base, obstaculos) {
  const caixa = { x: base.x, y: base.y, largura: base.largura, altura: base.altura };
  const livre = maiorEspacoLivre(caixa, obstaculos, { dentro: base.forma, folga: MEDIDA.GAP });
  const cabe = livre && livre.largura >= CABECALHO_DENTRO_MINIMO.largura && livre.altura >= CABECALHO_DENTRO_MINIMO.altura;
  if (!cabe) return placaAcimaDaSala(base);
  const hk = escalaDoCabecalho(livre);
  const alturaCabecalho = Math.min(MEDIDA.CABECALHO * hk, livre.altura);
  return {
    hk,
    cabecalho: { x: livre.x, y: livre.y, largura: livre.largura, altura: alturaCabecalho },
    fila: {
      x: livre.x + MEDIDA.PAD,
      y: livre.y + alturaCabecalho,
      largura: Math.max(0, livre.largura - 2 * MEDIDA.PAD),
      altura: Math.max(0, livre.altura - alturaCabecalho - MEDIDA.PAD),
    },
  };
}

function montarFilaDesenhada(fila, rect, m, base) {
  const conteudo = rect
    ? {
      x: rect.x + m.areaPad,
      y: rect.y + m.rotulo,
      largura: rect.largura - 2 * m.areaPad,
      altura: rect.altura - m.rotulo - m.areaPad,
    }
    : null;
  const grade = conteudo && conteudo.altura >= LINHAS_FILA_DESENHADA * m.pecaA
    ? gradeDeVagas(conteudo, m.pecaL, m.pecaA, m.pecaGap)
    : [];
  // Sem espaço para a fila, ela vira o "+N" de emergência no canto da sala:
  // o que chegou pela planilha nunca some sem um número que o conte.
  const emergencia = grade.length ? null : vagaDeEmergencia(base);
  const vagas = grade.length ? grade : emergencia ? [emergencia] : [];
  const onde = grade.length ? rect : emergencia || { x: base.x, y: base.y, largura: 0, altura: 0 };
  return {
    ...fila,
    ...onde,
    semMoldura: !grade.length,
    bancadas: [],
    bancadasOcultas: 0,
    bancadasOcultasIds: [],
    vagasChao: { conteiner: CONTEINER_FILA, vagas },
  };
}

/**
 * A sala montada A PARTIR DO DESENHO do gestor. Subsetores e postos ficam
 * onde ele os pôs; só a escala dos itens (fichas da fila e do chão) é
 * escolhida, pela mesma nota da montagem automática.
 */
function montarSalaDesenhada(base, areas, contagens) {
  const fila = areas.find((a) => a.tipo === TIPO_AREA.FILA);
  const doDesenho = (m) => [
    ...areas.filter((a) => a.tipo === TIPO_AREA.SUBSETOR).map((a) => montarAreaDesenhada(a, m, contagens)),
    postosSoltosDesenhados(areas, contagens),
  ].filter(Boolean);
  const obstaculos = doDesenho(medidasEm(1)).flatMap((a) => (a.semMoldura ? a.bancadas : [a]));
  const espaco = espacoDoCabecalhoEFila(base, obstaculos);
  let melhor = null;
  ESCALAS.forEach((k) => {
    const m = medidasEm(k);
    const montadas = [...doDesenho(m), montarFilaDesenhada(fila, espaco.fila, m, base)];
    const nota = pontuarTentativa(montadas, contagens, k);
    if (!melhor || nota > melhor.nota + 1e-9) melhor = { nota, k, areas: montadas };
  });
  return {
    k: melhor.k,
    areas: melhor.areas,
    hk: espaco.hk,
    cabecalho: espaco.cabecalho,
    cabecalhoFora: Boolean(espaco.fora),
  };
}

// ---------------------------------------------------------- planta

function pontoNaBorda(rect, alvo) {
  const c = centro(rect);
  const dx = alvo.x - c.x;
  const dy = alvo.y - c.y;
  if (dx === 0 && dy === 0) return c;
  const t = Math.min(
    dx !== 0 ? rect.largura / 2 / Math.abs(dx) : Number.POSITIVE_INFINITY,
    dy !== 0 ? rect.altura / 2 / Math.abs(dy) : Number.POSITIVE_INFINITY
  );
  const distancia = Math.hypot(dx, dy);
  const folga = FOLGA_CONECTOR / distancia;
  return { x: c.x + dx * (t + folga), y: c.y + dy * (t + folga) };
}

function sobrepoem(a, b) {
  return a.x < b.x + b.largura && b.x < a.x + a.largura && a.y < b.y + b.altura && b.y < a.y + a.altura;
}

const caixaDoElemento = (elemento) => ({
  setorId: elemento.setorId,
  ...(caixaEnvolvente(elemento.forma) || { x: 0, y: 0, largura: 0, altura: 0 }),
});

/**
 * Corredores do fluxo (sala → próxima sala), da borda de uma à da outra.
 * `elementos` já vem na ordem do fluxo (`layout.js`), então basta ligar cada um
 * ao seguinte — nenhum código de setor fixo aqui.
 */
export function conectoresDoFluxo(elementos) {
  const caixas = (elementos || []).map(caixaDoElemento);
  return caixas.slice(1).flatMap((para, i) => {
    const de = caixas[i];
    if (sobrepoem(de, para)) return [];
    const inicio = pontoNaBorda(de, centro(para));
    const fim = pontoNaBorda(para, centro(de));
    return [{
      id: `${de.setorId}->${para.setorId}`,
      de: de.setorId,
      para: para.setorId,
      x1: inicio.x,
      y1: inicio.y,
      x2: fim.x,
      y2: fim.y,
    }];
  });
}

// Vão da porta (mundo): o bastante para uma bancada passar.
const LARGURA_PORTA = 56;

/** O trecho da parede de `rect` mais perto de `ponto` — onde fica a porta. */
function portaNaParede(rect, ponto) {
  const dEsquerda = Math.abs(ponto.x - rect.x);
  const dDireita = Math.abs(ponto.x - (rect.x + rect.largura));
  const dTopo = Math.abs(ponto.y - rect.y);
  const dBase = Math.abs(ponto.y - (rect.y + rect.altura));
  const menor = Math.min(dEsquerda, dDireita, dTopo, dBase);
  const vertical = menor === dEsquerda || menor === dDireita;
  const comprimento = Math.min(LARGURA_PORTA, (vertical ? rect.altura : rect.largura) * 0.6);
  if (vertical) {
    const x = menor === dEsquerda ? rect.x : rect.x + rect.largura;
    const y = limitar(ponto.y, rect.y + comprimento / 2, rect.y + rect.altura - comprimento / 2);
    return { x1: x, y1: y - comprimento / 2, x2: x, y2: y + comprimento / 2 };
  }
  const y = menor === dTopo ? rect.y : rect.y + rect.altura;
  const x = limitar(ponto.x, rect.x + comprimento / 2, rect.x + rect.largura - comprimento / 2);
  return { x1: x - comprimento / 2, y1: y, x2: x + comprimento / 2, y2: y };
}

/**
 * Vãos de porta: onde o corredor do fluxo (e a entrada da fábrica) encosta na
 * parede, ela se abre — é o que faz a planta ser lida como fábrica, e não como
 * cartões soltos.
 *
 * A porta é calculada na caixa ENVOLVENTE. Num polígono o vão pode cair num
 * trecho em que não há parede desenhada e aí ele simplesmente não aparece: o
 * risco de "não abrir" é melhor que o de rasgar a parede num canto qualquer.
 */
export function portasDoFluxo(elementos, { conectores = [], entrada = null } = {}) {
  const porSetor = new Map((elementos || []).map((e) => [e.setorId, caixaDoElemento(e)]));
  const portas = [];
  conectores.forEach((c) => {
    const de = porSetor.get(c.de);
    const para = porSetor.get(c.para);
    if (de) portas.push({ id: `${c.id}:saida`, ...portaNaParede(de, { x: c.x1, y: c.y1 }) });
    if (para) portas.push({ id: `${c.id}:chegada`, ...portaNaParede(para, { x: c.x2, y: c.y2 }) });
  });
  const primeira = (elementos || [])[0];
  if (entrada && primeira) portas.push({ id: "entrada", ...portaNaParede(caixaDoElemento(primeira), entrada) });
  return portas;
}

/** De onde chegam os ambientes: à esquerda da primeira sala do fluxo. */
export function pontoDeEntrada(elementos) {
  const primeira = (elementos || [])[0];
  if (!primeira) return { x: 0, y: 0 };
  const caixa = caixaDoElemento(primeira);
  return { x: caixa.x - DISTANCIA_ENTRADA, y: caixa.y + caixa.altura / 2 };
}

function agruparPorSetor(itens, setorDe) {
  const mapa = new Map();
  itens.forEach((item) => {
    const setorId = setorDe(item);
    if (!setorId) return;
    if (!mapa.has(setorId)) mapa.set(setorId, []);
    mapa.get(setorId).push(item);
  });
  return mapa;
}

/**
 * Planta inteira: cada sala montada com os ambientes e as peças do seu setor.
 *
 * `combinadas` = saída de combinarDemandas (todas as situações; só as de setor
 * entram); `pecas` = saída de combinarPecas; `passa`/`passaPeca` = Set do
 * filtro (null = sem filtro).
 * → {salas, ambientes, pecas, mais, posicoes, conectores, entrada, portas}
 */
export function montarPlanta(
  elementos,
  { combinadas = [], pecas = [], estado = null, passa = null, passaPeca = null } = {}
) {
  const ambientesPorSetor = agruparPorSetor(
    combinadas.filter((d) => d.situacao === SITUACAO.SETOR),
    (d) => d.setorId
  );
  const pecasPorSetor = agruparPorSetor(pecas, (p) => p.setorId);
  const salas = [];
  const fichas = [];
  const pecinhas = [];
  const mais = [];
  const posicoes = new Map();
  (elementos || []).forEach((elemento) => {
    const montada = montarSala(elemento, {
      demandas: ordenarParaExibir(ambientesPorSetor.get(elemento.setorId) || [], passa),
      pecas: ordenarPecasParaExibir(pecasPorSetor.get(elemento.setorId) || [], passaPeca),
      estado,
    });
    salas.push(montada.sala);
    fichas.push(...montada.ambientes);
    pecinhas.push(...montada.pecas);
    mais.push(...montada.mais);
    montada.posicoes.forEach((pos, chave) => posicoes.set(chave, pos));
  });
  const conectores = conectoresDoFluxo(elementos);
  const entrada = pontoDeEntrada(elementos);
  return {
    salas,
    ambientes: fichas,
    pecas: pecinhas,
    mais,
    posicoes,
    conectores,
    entrada,
    portas: portasDoFluxo(elementos, { conectores, entrada }),
  };
}
